import { Button, Menu, MenuItem, Popover, Intent } from "@snpjs/core";
import { Download, FileCsv, MicrosoftExcelLogo } from "@snpjs/icons";
import * as XLSX from "xlsx";
import { useEffect, useState } from "react";

import { AdminViewApi } from "../../api/AdminViewApi";
import { ILicenseLogEntry } from "../../shared/model/ILicenseLogEntry";
import { Table2 } from "../../components/table2/Table2";
import { texts } from "../../i18n/texts";
import { useRequest } from "../../hooks/useRequest";
import useTranslation from "../../hooks/useTranslation";
import styles from "./AdminViewSection.module.scss";

export const ProductMappingSection: React.FC = () => {
  const { t } = useTranslation();
  const request = useRequest();
  const [productMappingData, setProductMappingData] = useState<ILicenseLogEntry[]>([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [visibleCount, setVisibleCount] = useState<number>(0);

  const fetchProductMapping = async () => {
    setIsLoading(true);
    await request.send(async () => {
      const data = await AdminViewApi.getProductMapping();
      setProductMappingData(data);
      setHasLoaded(true);
    });
    setIsLoading(false);
  };

  const exportData = (format: "csv" | "excel", rows: ILicenseLogEntry[] = productMappingData) => {
    if (rows.length === 0) return;

    const columns = Object.keys(rows[0] ?? {});
    const escapeCell = (value: unknown): string => {
      const text = value == null ? "" : String(value).replace(/\r?\n/g, " ");
      return `"${text.replace(/"/g, '""')}"`;
    };

    if (format === "csv") {
      const csv = [
        columns.join(","),
        ...rows.map((row) => columns.map((column) => escapeCell((row as Record<string, unknown>)[column])).join(",")),
      ].join("\n");

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `product_mapping.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      return;
    }

    const workbook = XLSX.utils.book_new();
    const sheetData = rows.map((row) => {
      const values: Record<string, unknown> = {};
      columns.forEach((column) => {
        values[column] = (row as Record<string, unknown>)[column] ?? "";
      });
      return values;
    });

    const worksheet = XLSX.utils.json_to_sheet(sheetData, { header: columns });
    XLSX.utils.book_append_sheet(workbook, worksheet, "ProductMapping");

    const workbookBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([workbookBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `product_mapping.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportMenu = (table?: any) => (
    <Menu>
      <MenuItem
        icon={<FileCsv />}
        text="CSV"
        onClick={() => exportData("csv", table ? table.getFilteredRowModel().rows.map((row: any) => row.original as ILicenseLogEntry) : productMappingData)}
      />
      <MenuItem
        icon={<MicrosoftExcelLogo />}
        text="Excel"
        onClick={() => exportData("excel", table ? table.getFilteredRowModel().rows.map((row: any) => row.original as ILicenseLogEntry) : productMappingData)}
      />
    </Menu>
  );

  useEffect(() => {
    void fetchProductMapping();
  }, []);

  return (
    <div className={styles.wrapper}>
      {hasLoaded && (
        <>
          <div className={styles.countHeader}>{visibleCount} Records Found</div>
          <div className={styles.tableContainer}>
            <Table2
              data={productMappingData}
              isLoading={isLoading}
              noResultsTitle={t(texts.adminView.noProductMappingData)}
              onVisibleCountChange={(count) => setVisibleCount(count)}
              showUtilityToolbar={false}
              enableGlobalFilter={false}
              leftToolbar={(table) => (
                <Popover content={exportMenu(table)} placement="bottom-start" usePortal={false}>
                  <Button
                    intent={Intent.PRIMARY}
                    icon={<Download />}
                    rightIcon="caret-down"
                    text="Export"
                    fill
                    alignText="left"
                  />
                </Popover>
              )}
            />
          </div>
        </>
      )}
    </div>
  );
};
